// Client de l'API des trajets, et fusion des listes locale et serveur.
//
// Principe : le serveur est une bibliothèque partagée, pas la source de vérité. Tout appel
// qui échoue laisse l'application pleinement utilisable sur ses trajets locaux — c'est la
// condition pour qu'une tournée puisse partir d'un stationnement sans réseau.

const CODE_KEY = 'bustracker.apiCode';
const CODE_HEADER = 'X-BusTracker-Code';

// Un lien mobile qui traîne doit échouer, pas figer l'écran de choix de tournée pendant
// une minute au moment où le chauffeur veut partir.
const TIMEOUT_MS = 8000;

// Code refusé par le serveur : l'interface doit le redemander.
class ApiAuthError extends Error {
    constructor(message) { super(message || "Code d'accès refusé"); this.name = 'ApiAuthError'; }
}

// Serveur injoignable (hors réseau, délai dépassé, panne) : silencieux, on reste en local.
class ApiOfflineError extends Error {
    constructor(message) { super(message || 'Serveur injoignable'); this.name = 'ApiOfflineError'; }
}

// ---------------------------------------------------------------- code d'accès

function getCode() {
    try { return localStorage.getItem(CODE_KEY) || ''; } catch (e) { return ''; }
}

function setCode(code) {
    try {
        if (code) localStorage.setItem(CODE_KEY, code);
        else localStorage.removeItem(CODE_KEY);
    } catch (e) { /* stockage refusé (navigation privée) : l'app marche sans serveur */ }
}

const hasCode = () => !!getCode();

// Identifiant du trajet, généré par le CLIENT au moment de l'enregistrement local, puis
// utilisé tel quel comme identifiant serveur (PUT /api/trips/<id>, un upsert). C'est ce qui
// rend l'envoi rejouable : un double appui, ou un renvoi après un délai dépassé qui avait
// en fait abouti, retombe sur la même tournée au lieu d'en créer une seconde.
// Le repli couvre les contextes sans crypto.randomUUID ; ID_RE du serveur l'accepte aussi.
function newSyncId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'k' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
}

// ---------------------------------------------------------------- appels

async function request(path, options) {
    const opts = options || {};
    const code = getCode();
    if (!code) throw new ApiAuthError("Aucun code d'accès enregistré");

    const init = {
        method: opts.method || 'GET',
        headers: { [CODE_HEADER]: code },
        cache: 'no-store'
    };
    if (opts.body !== undefined) {
        init.headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(opts.body);
    }
    // AbortSignal.timeout manque sur iOS < 16 : sans lui on part sans délai plutôt que de
    // lever un TypeError qui ferait planter l'écran des tournées.
    if (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) {
        init.signal = AbortSignal.timeout(TIMEOUT_MS);
    }

    let res;
    try {
        res = await fetch(path, init);
    } catch (e) {
        // TypeError (réseau) comme AbortError/TimeoutError (délai) : de notre point de vue,
        // hors ligne.
        throw new ApiOfflineError(e && e.name === 'TimeoutError' ? 'Serveur trop lent' : 'Serveur injoignable');
    }

    if (res.status === 401) throw new ApiAuthError();

    // Garde-fou indispensable. Le projet ne fait jamais de skipWaiting() : après un
    // déploiement, l'ANCIEN service worker reste actif jusqu'au redémarrage manuel de
    // version, et lui ne connaît pas le contournement de /api/ — hors réseau il répond la
    // coquille /index.html avec un code 200. Une page HTML 413 ou 502 de nginx arrive de
    // la même façon. Sans ce test, JSON.parse exploserait sur « <!DOCTYPE ».
    const ctype = res.headers.get('content-type') || '';
    if (ctype.indexOf('application/json') === -1) {
        throw new ApiOfflineError('Réponse inattendue du serveur');
    }

    if (!res.ok) {
        let message = 'HTTP ' + res.status;
        try {
            const body = await res.json();
            if (body && body.error) message = body.error;
        } catch (e) { /* le code HTTP suffit */ }
        // 5xx : le serveur répond mais ne peut pas servir — même traitement qu'une panne.
        if (res.status >= 500) throw new ApiOfflineError(message);
        throw new Error(message);
    }
    return res.json();
}

const tripUrl = id => '/api/trips/' + encodeURIComponent(id);

const apiListTrips = () => request('/api/trips');
const apiGetTrip = id => request(tripUrl(id));
const apiPutTrip = (id, trip) => request(tripUrl(id), { method: 'PUT', body: trip });
const apiDeleteTrip = id => request(tripUrl(id), { method: 'DELETE' });

// ---------------------------------------------------------------- fusion des listes

// local  : projection de listTrips() — { id, name, stopCount, createdAt, syncId, remoteId }
//          où id est le nombre auto-incrémenté d'IndexedDB et syncId la chaîne partagée.
// remote : liste du serveur, ou null quand il est injoignable (on ne sait alors rien de
//          plus que ce que les trajets locaux portent déjà).
//
// Renvoie une ligne par tournée, jamais un doublon local/serveur côte à côte :
//   'synced' présent des deux côtés · 'local' local seulement · 'remote' serveur seulement
//
// localId (nombre) et remoteId (chaîne) restent SÉPARÉS : les confondre ferait appeler
// getTrip('<uuid>') sur IndexedDB, qui renvoie undefined sans lever la moindre erreur.
//
// L'appariement se fait sur les identifiants, JAMAIS sur le nom : deux appareils qui
// enregistrent chacun leur « Tournée du 23 » ont bien parcouru deux trajets différents.
function mergeTripLists(local, remote) {
    const locals = Array.isArray(local) ? local : [];
    const rows = [];

    if (!Array.isArray(remote)) {
        for (const t of locals) {
            rows.push({
                key: 'l' + t.id, name: t.name, stopCount: t.stopCount, createdAt: t.createdAt,
                localId: t.id, syncId: t.syncId || null,
                // Hors ligne, un remoteId déjà posé reste une preuve d'envoi réussi.
                remoteId: t.remoteId || null,
                state: t.remoteId ? 'synced' : 'local'
            });
        }
        return rows.sort((a, b) => b.createdAt - a.createdAt);
    }

    const byId = new Map();
    for (const r of remote) byId.set(r.id, r);

    const claimed = new Set();
    for (const t of locals) {
        const match = t.syncId ? byId.get(t.syncId) : null;
        if (match) claimed.add(match.id);
        rows.push({
            key: 'l' + t.id, name: t.name, stopCount: t.stopCount, createdAt: t.createdAt,
            localId: t.id, syncId: t.syncId || null,
            remoteId: match ? match.id : null,
            // Une tournée supprimée du serveur par un autre appareil redevient « à
            // envoyer » : c'est l'état vrai, et le bouton de renvoi permet de la repartager.
            state: match ? 'synced' : 'local'
        });
    }

    for (const r of remote) {
        if (claimed.has(r.id)) continue;
        rows.push({
            key: 'r' + r.id, name: r.name, stopCount: r.stopCount, createdAt: r.createdAt,
            localId: null, syncId: r.id, remoteId: r.id, state: 'remote'
        });
    }

    return rows.sort((a, b) => b.createdAt - a.createdAt);
}

export {
    ApiAuthError, ApiOfflineError,
    getCode, setCode, hasCode, newSyncId,
    apiListTrips, apiGetTrip, apiPutTrip, apiDeleteTrip,
    mergeTripLists
};
